import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
const ext = (m) => (m.includes("mp4") ? "m4a" : "webm");

// Lecture des réponses (TTS OpenAI via backend). unlock() doit être appelé dans un geste utilisateur (iOS).
export function useSpeaker() {
  const audioRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const get = () => { if (!audioRef.current) audioRef.current = new Audio(); return audioRef.current; };
  const unlock = () => { const a = get(); a.muted = true; a.play().catch(() => {}); a.pause(); a.muted = false; };
  const stop = () => { const a = get(); a.pause(); a.currentTime = 0; setSpeaking(false); };
  const speak = async (text) => {
    if (!text) return;
    setLoading(true);
    try {
      const r = await api.post("/ops/stripe-ai/voice/tts", { text: text.slice(0, 4000) });
      const a = get();
      a.src = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      a.onended = () => setSpeaking(false);
      a.onerror = () => setSpeaking(false);
      setSpeaking(true);
      await a.play();
    } catch (e) { setSpeaking(false); toast.error(e?.response?.data?.detail || "Lecture vocale impossible"); }
    finally { setLoading(false); }
  };
  return { speak, stop, speaking, loading, unlock };
}

// Bouton micro : appuie → enregistre ; appuie encore → transcription Whisper → onTranscript(texte)
export function MicButton({ onTranscript, disabled, onPress }) {
  const recRef = useRef(null);
  const chunks = useRef([]);
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    onPress?.();
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error("Micro non disponible sur ce navigateur"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MIMES.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        if (blob.size < 1500) { toast.error("Enregistrement trop court"); return; }
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `question.${ext(type)}`);
          const r = await api.post("/ops/stripe-ai/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (!r.data.text) { toast.error("Je n'ai rien compris — réessaie"); return; }
          onTranscript(r.data.text);
        } catch (e) { toast.error(e?.response?.data?.detail || "Transcription échouée"); }
        finally { setBusy(false); }
      };
      mr.start();
      recRef.current = mr;
      setRec(true);
    } catch { toast.error("Accès au micro refusé"); }
  };
  const stop = () => { recRef.current?.stop(); setRec(false); };
  return (
    <button type="button" onClick={rec ? stop : start} disabled={disabled || busy} title={rec ? "Appuie pour envoyer" : "Poser ma question à la voix"}
      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors disabled:opacity-40 ${rec ? "bg-rose-600 text-white animate-pulse" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"}`}
      data-testid="stripe-ai-mic">
      {busy ? "…" : rec ? "⏹ Envoyer" : "🎙"}
    </button>
  );
}
